import { grey60 } from "../../Content/COREColour";
import { ChartDataType, COREChartProps, MarginObject } from "../COREChart";
import { Layout, LayoutAxis } from "plotly.js/dist/plotly-myPlotly.js";
import ImgCOREMarketsLogoSVG from "../../../assets/img/COREMarketsLogoForDownload.svg";

type LayoutAxisWithDynamicAxis = LayoutAxis & Record<string, any>;

const timeSeries: Partial<Plotly.RangeSelector> = {
  buttons: [
    {
      count: 7,
      label: "1W",
      step: "day",
      stepmode: "backward",
    },
    {
      count: 1,
      label: "1M",
      step: "month",
      stepmode: "backward",
    },
    {
      count: 3,
      label: "1Q",
      step: "month",
      stepmode: "backward",
    },
    {
      count: 1,
      label: "1Y",
      step: "year",
      stepmode: "backward",
    },
  ],
};

const axisPositionConfig = {
  rangemode: "normal",
  automargin: true,
};

const axisStyleConfig = {
  showline: true,
  zeroline: false,
  gridwidth: 1,
  linecolor: grey60,
  linewidth: 1,
  mirror: true,
};

const setupAxisConfiguration = (
  axis: Partial<LayoutAxis>,
  showGrid: boolean,
  customAxisPosition: boolean
) => {
  const standoff = 8;
  const title =
    typeof axis.title === "string"
      ? {
          text: axis.title,
          standoff,
        }
      : {
          ...axis.title,
          standoff,
        };
  const resultConfig = {
    ...axis,
    ...axisStyleConfig,
    // Pass consumer's gridcolor through (or undefined) so mergedLayout can fall back to the CSS var
    gridcolor: axis.gridcolor,
    title,
    ...(axis.showgrid !== undefined
      ? { showgrid: axis.showgrid }
      : showGrid
      ? { showgrid: true }
      : { showgrid: false }),
    ...(!customAxisPosition && axisPositionConfig),
  } as Partial<LayoutAxis>;
  if (!customAxisPosition) {
    delete resultConfig.tickformat;
    delete resultConfig.domain;
    delete resultConfig.position;
  }
  return resultConfig;
};

const isAxisKey = (key: string): key is `xaxis${string}` | `yaxis${string}` => {
  return key.startsWith("xaxis") || key.startsWith("yaxis");
};

const layoutOptionPlotly = (
  useResizeHandler: COREChartProps["useResizeHandler"],
  layout: COREChartProps["layout"],
  downloadPngCanvas: COREChartProps["downloadPngCanvas"],
  chartMargin: MarginObject
) => {
  // currentYRange will be [] if props.useAutoScaleYAxis have value
  const customConfigXAxis = configXAxisLayout(layout);

  return {
    // eslint-disable-next-line camelcase
    paper_bgcolor: "#FFFFFF",
    margin: chartMargin,
    images: !downloadPngCanvas
      ? ([
          {
            x: 1.09,
            y: -0.55,
            sizex: 0.3,
            sizey: 0.25,
            source: ImgCOREMarketsLogoSVG,
            xref: "paper",
            yref: "paper",
            xanchor: "right",
            yanchor: "bottom",
            opacity: 1,
            layer: "below",
            visible: true,
          },
        ] as Layout["images"])
      : [],
    hovermode: layout.hovermode || "x",
    autosize: useResizeHandler || true,
    ...layout,
    ...customConfigXAxis,
  };
};

const configXAxisLayout = (
  layout: COREChartProps["layout"]
): Partial<Layout> => {
  const preXAxis = layout?.xaxis;

  return {
    xaxis: {
      ...preXAxis,
      showline: preXAxis?.showline || true,
      showgrid: preXAxis?.showgrid || false,
      title: preXAxis?.title
        ? preXAxis?.title
        : {
            text: "",
          },
    },
  };
};

export const setupLayout: (
  layout: COREChartProps["layout"],
  fitParent: COREChartProps["fitParent"],
  showXGrid: boolean,
  showYGrid: boolean,
  useResizeHandler: COREChartProps["useResizeHandler"],
  downloadPngCanvas: COREChartProps["downloadPngCanvas"],
  margin?: COREChartProps["margin"],
  isCustomLayout?: COREChartProps["customLayout"],
  showTimeButtons?: COREChartProps["showTimeButtons"]
) => Partial<Layout> = (
  layout,
  fitParent,
  showXGrid,
  showYGrid,
  useResizeHandler,
  downloadPngCanvas,
  margin = { l: 2, t: 2, r: 2, b: 2 },
  isCustomLayout = false,
  showTimeButtons = true
) => {
  const formatedLayout: Partial<Layout> = layoutOptionPlotly(
    useResizeHandler,
    layout,
    downloadPngCanvas,
    margin
  );
  if (showTimeButtons && formatedLayout["xaxis"])
    formatedLayout["xaxis"]["rangeselector"] = timeSeries;

  if (fitParent) {
    delete formatedLayout.width;
    delete formatedLayout.height;
  }

  if (!isCustomLayout) {
    delete formatedLayout.margin;
    const hasRightAxis =
      Object.keys(formatedLayout).filter(
        (key) =>
          key.startsWith("yaxis") &&
          (formatedLayout as LayoutAxisWithDynamicAxis)[key].side === "right"
      ).length > 0;
    formatedLayout.margin = {
      t: 16,
      l: 65,
      r: hasRightAxis ? 65 : 2,
      b: 65,
    };
  }

  Object.keys(formatedLayout).forEach((key) => {
    if (!isAxisKey(key)) return;
    const isX = key.startsWith("xaxis");
    const showGrid = isX ? showXGrid : showYGrid;
    (formatedLayout as LayoutAxisWithDynamicAxis)[key] = setupAxisConfiguration(
      (formatedLayout as LayoutAxisWithDynamicAxis)[key],
      showGrid,
      isCustomLayout
    );
  });
  return formatedLayout;
};

export const applyCustomTooltipConfig = (
  data: Partial<ChartDataType>[]
): Partial<ChartDataType>[] => {
  return data.map((trace) => ({
    ...trace,
    hoverinfo: "none",
    hovertemplate: "",
  }));
};
